import { Card } from '@/components/ui/Card'
import { GateBadge } from './GateBadge'
import type { CategoriaResult, GateStatus } from './types'
import { Trophy } from 'lucide-react'

export interface SimulatorCardProps {
  bonusResult: {
    categorias: CategoriaResult[]
    totalSalarios: number
    totalMonetario: number
    gateBlocked: boolean
    gates: Record<string, GateStatus>
  }
  salarioBase: number
  employeeName: string
  /** GAP 5: Publish flags for director view filtering */
  publicados?: Record<string, boolean>
  /** When true, show all categories (RH view) */
  isRH?: boolean
}

export const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)

export const formatSalarios = (value: number): string =>
  `${value.toFixed(2)} salários`

export function SimulatorCard({ bonusResult, employeeName, publicados, isRH }: SimulatorCardProps) {
  // Filter categories for director view
  const visibleCategorias = isRH || !publicados
    ? bonusResult.categorias
    : bonusResult.categorias.filter(cat => publicados[cat.id] === true)

  const totalVisible = visibleCategorias.length
  const totalAll = bonusResult.categorias.length
  const isPartial = totalVisible < totalAll && !isRH

  const partialSalarios = visibleCategorias.reduce((sum, c) => sum + c.bonusSalarios, 0)
  const partialMonetario = isRH ? bonusResult.totalMonetario : 0 // Directors don't see monetary total when partial

  return (
    <Card className="p-6" data-testid="simulator-card">
      <div className="flex items-center gap-3 mb-6">
        <Trophy className="text-brand-primary" size={24} />
        <h3 className="text-lg font-display font-bold text-white">
          Simulador de Bonus — {employeeName}
        </h3>
      </div>

      <div className="table-container">
        <table className="table">
          <thead>
            <tr className="table-header">
              <th className="text-left">Categoria</th>
              <th className="text-right">Atingimento</th>
              <th className="text-right">Salarios</th>
              <th className="text-right">Status</th>
            </tr>
          </thead>
          <tbody>
            {visibleCategorias.map((cat) => {
              const gateInfo = bonusResult.gates[cat.id]
              return (
                <tr key={cat.id} className="table-row" data-testid={`simulator-row-${cat.id}`}>
                  <td className="text-white">
                    {cat.nome} ({cat.peso}%)
                  </td>
                  <td className="text-right text-brand-gray">
                    {cat.gateBlocked ? '—' : `${cat.media.toFixed(1)}%`}
                  </td>
                  <td className="text-right text-white">
                    {cat.gateBlocked ? '—' : cat.bonusSalarios.toFixed(2)}
                  </td>
                  <td className="text-right">
                    <GateBadge
                      blocked={cat.gateBlocked}
                      gateInfo={gateInfo ? { ref: gateInfo.ref, min: gateInfo.min, atual: gateInfo.atual } : undefined}
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Total section */}
      <div className="mt-6 p-4 rounded-lg bg-brand-dark-light border border-brand-gray/20" data-testid="simulator-total">
        <div className="text-center">
          <p className="metric-value text-brand-primary text-3xl font-bold">
            {formatSalarios(isRH ? bonusResult.totalSalarios : partialSalarios)}
          </p>
          {(isRH || !isPartial) && (
            <p className="metric-label text-lg text-white mt-1">
              {formatCurrency(isRH ? bonusResult.totalMonetario : partialMonetario)}
            </p>
          )}
          {isPartial && (
            <p className="text-xs text-brand-gray mt-2">
              Parcial — {totalVisible} de {totalAll} categorias publicadas
            </p>
          )}
        </div>
      </div>
    </Card>
  )
}
